/**
 * Scansione dei documenti nel browser.
 *
 * Tutto il lavoro avviene sul dispositivo: la foto del documento non lascia
 * mai il browser, al backend arrivano solo i dati letti e confermati
 * dall'operatore.
 *
 * Le librerie (tesseract.js per l'OCR, zxing-wasm per i codici a barre) e il
 * modello OCR della MRZ sono nei file statici del backend, in
 * statics/docscan, e si caricano solo alla prima scansione: chi non usa il
 * modulo non scarica nulla. Il modello resta poi nella cache del browser.
 */

import { leggiImmagine } from "./pipeline";

const CARATTERI_MRZ = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<";

let base = "";
const script = {};
let workerOcr = null;
let workerTesto = null;
let zxingPronto = null;

/** Carica uno script una volta sola. */
function caricaScript(url) {
  if (!script[url]) {
    script[url] = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = url;
      s.async = true;
      s.onload = resolve;
      s.onerror = () => {
        delete script[url];
        reject(new Error("Impossibile caricare " + url));
      };
      document.head.appendChild(s);
    });
  }
  return script[url];
}

/** Worker OCR con il modello della MRZ, creato alla prima richiesta. */
async function ocr() {
  if (!workerOcr) {
    workerOcr = (async () => {
      await caricaScript(base + "/tesseract/tesseract.min.js");
      const worker = await window.Tesseract.createWorker("mrz", 1, {
        workerPath: base + "/tesseract/worker.min.js",
        corePath: base + "/tesseract/core",
        langPath: base + "/lang"
      });
      await worker.setParameters({ tessedit_char_whitelist: CARATTERI_MRZ });
      return worker;
    })();
    workerOcr.catch(() => {
      workerOcr = null;
    });
  }
  return workerOcr;
}

/**
 * Worker OCR per il testo stampato (patenti), con il modello italiano: si
 * crea solo quando una foto non ha la MRZ, cosi' chi scansiona solo
 * passaporti e carte d'identita' non lo scarica mai.
 */
async function ocrTesto() {
  if (!workerTesto) {
    workerTesto = (async () => {
      await caricaScript(base + "/tesseract/tesseract.min.js");
      return window.Tesseract.createWorker("ita", 1, {
        workerPath: base + "/tesseract/worker.min.js",
        corePath: base + "/tesseract/core",
        langPath: base + "/lang"
      });
    })();
    workerTesto.catch(() => {
      workerTesto = null;
    });
  }
  return workerTesto;
}

/** Lettore di codici a barre, con il wasm scaricato dal backend. */
async function zxing() {
  if (!zxingPronto) {
    zxingPronto = (async () => {
      await caricaScript(base + "/zxing/zxing-reader.js");
      // Il wasm si passa gia' scaricato: non dipende dal tipo MIME con cui il
      // server lo restituisce.
      const risposta = await fetch(base + "/zxing/zxing_reader.wasm");
      if (!risposta.ok) {
        throw new Error("Impossibile caricare il lettore di codici a barre");
      }
      window.ZXingWASM.prepareZXingModule({
        overrides: { wasmBinary: await risposta.arrayBuffer() },
        fireImmediately: true
      });
      return window.ZXingWASM;
    })();
    zxingPronto.catch(() => {
      zxingPronto = null;
    });
  }
  return zxingPronto;
}

/** Righe lette dall'OCR, con la loro posizione. */
function righe(data) {
  const out = [];
  for (const b of data.blocks || []) {
    for (const p of b.paragraphs) {
      for (const l of p.lines) {
        out.push({ testo: l.text.trim(), x0: l.bbox.x0, y0: l.bbox.y0, x1: l.bbox.x1, y1: l.bbox.y1 });
      }
    }
  }
  return out;
}

/**
 * Bianco e nero fatto da tesseract: "adattiva" e' Sauvola (soglia per zone),
 * altrimenti Otsu su tutta l'immagine. Il parametro resta nel worker: si
 * passa sempre.
 */
function sogliaTesseract(soglia) {
  return soglia === "adattiva" ? "2" : "0";
}

function ambiente(avanzamento, sfocata) {
  return {
    canvas(w, h) {
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      // Le immagini si rileggono piu' volte (scala di grigi, bianco e nero):
      // il browser le tiene allora in memoria normale invece che sulla GPU.
      c.getContext("2d", { willReadFrequently: true });
      return c;
    },
    async ocr(canvas, { psm, soglia }) {
      const worker = await ocr();
      await worker.setParameters({ tessedit_pageseg_mode: String(psm), thresholding_method: sogliaTesseract(soglia) });
      const { data } = await worker.recognize(canvas, {}, { blocks: true });
      return righe(data);
    },
    async ocrTesto(canvas, { psm, soglia }) {
      const worker = await ocrTesto();
      await worker.setParameters({ tessedit_pageseg_mode: String(psm), thresholding_method: sogliaTesseract(soglia) });
      const { data } = await worker.recognize(canvas, {}, { blocks: true });
      return righe(data);
    },
    async barcode(canvas) {
      const z = await zxing();
      const img = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height);
      const letti = await z.readBarcodes(img, {
        formats: ["PDF417", "Code128", "Code39", "QRCode", "DataMatrix"],
        tryHarder: true,
        maxNumberOfSymbols: 4
      });
      return letti.filter((r) => r.isValid).map((r) => ({ formato: r.format, testo: r.text }));
    },
    avanzamento,
    sfocata
  };
}

/** Immagine da un file (foto o scansione), con l'orientamento EXIF applicato. */
async function caricaImmagine(file) {
  if (window.createImageBitmap) {
    try {
      return await window.createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (e) {
      // Alcuni browser non accettano le opzioni: si ripiega sull'<img>.
    }
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Il file non e' un'immagine leggibile"));
    };
    img.src = url;
  });
}

export default {
  /**
   * Indica dove sono i file statici della scansione (una volta, all'avvio).
   *
   * @param {string} url indirizzo di statics/docscan sul backend
   */
  configura(url) {
    base = url.replace(/\/$/, "");
  },

  /** Scarica in anticipo librerie e modello, mentre l'operatore inquadra. */
  prepara() {
    return Promise.all([ocr(), zxing()]);
  },

  /**
   * Legge un documento da un file immagine.
   *
   * @param {File|Blob} file foto o scansione del documento
   * @param {function} avanzamento riceve i messaggi di avanzamento
   * @param {function} sfocata chiamata subito se la foto e' troppo sfocata
   */
  async leggi(file, avanzamento, sfocata) {
    const immagine = await caricaImmagine(file);
    try {
      const letto = await leggiImmagine(ambiente(avanzamento, sfocata), immagine);
      // Per tarare NITIDEZZA_MINIMA sulle foto vere dei telefoni.
      if (typeof console !== "undefined") {
        console.info("docscan: nitidezza " + letto.nitidezza + " (" + immagine.width + "x" + immagine.height + ")");
      }
      return letto;
    } finally {
      if (immagine.close) {
        immagine.close();
      }
    }
  }
};
